import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { getAPIClient } from "../../apiClient";
import { Squirrel } from "../../components/spinner/Squirrel";
import { NotFound } from "../../components/NotFound";
import { useTranslation } from "react-i18next";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  loadableCompleteVerifiedRecordsAtom,
  izProviderConfigsAtom,
  userAtom,
} from "../../jotai/atoms";

export default function SmartHealthCard() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [user] = useAtom(userAtom);
  const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const [qrCode, setQrCode] = useState("");
  const [loadingVCI, setLoadingVCI] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<string>();
  const isIOSMobile = /iphone|ipad/i.test(navigator.userAgent);

  const currRecord = React.useMemo(() => {
    if (allVerifiedRecords.state !== "hasData") {
      return null;
    }
    return allVerifiedRecords.data.find((record) => record.record.uid === id);
  }, [allVerifiedRecords, id]);

  const providerConfig = providerConfigs.find(
    (config) =>
      config.iz_provider.destination_id.toLowerCase() ===
      currRecord?.record?.izProviderKey.toLowerCase()
  );

  useEffect(() => {
    if (currRecord?.record && qrCode.length === 0 && !loadingVCI) {
      setLoadingVCI(true);
      const fetchQRCode = async () => {
        return await getAPIClient().getVCICode(
          currRecord.record.uid,
          currRecord.record.izProviderKey
        );
      };
      fetchQRCode()
        .then((code) => {
          setQrCode(code);
          setLoadingVCI(false);
        })
        .catch((error) => {
          setLoadingVCI(false);
          setShowErrorModal("Unable to fetch QR code. Please try again later.");
        });
    }
    return () => {};
  }, [qrCode, currRecord?.record]);

  if (allVerifiedRecords.state === "loading") {
    return <Squirrel />;
  }
  if (!currRecord?.record.uid || !providerConfig?.features.qr_code) {
    return <NotFound />;
  }

  return (
    <div className="max-md:mb-20 max-md:mt-6 max-md:mx-4 p-2 bg-qr-card-blue rounded-md flex flex-col gap-[8px] text-black">
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <div className="flex flex-col">
        <div className="flex gap-0.5">
          <p className="font-bold text-lg">COVID-19 Vaccination</p>
          <img src="/assets/images/Verified_Badge.png" className="w-[20px] h-[20px] my-auto" />
        </div>
        <div className="flex">
          <p className="text-lg font-bold">Name:</p>
          <p className="my-auto ml-[8px]">{`${user?.last_name}, ${user?.first_name}${
            user?.middle_name ? ` ${user.middle_name}` : ""
          }`}</p>
        </div>
        <div className="flex">
          <p className="text-lg font-bold">DOB:</p>
          <p className="my-auto ml-[18px]">{`${
            user?.dob ? DateTime.fromISO(user.dob).toLocaleString() : ""
          }`}</p>
        </div>
      </div>
      <div className="flex flex-col gap-[8px] mx-6">
        <p className="font-bold mx-auto">{`Verified: ${DateTime.now().toLocaleString()}`}</p>
        {qrCode.length > 0 ? (
          <div className="flex flex-col gap-0.5 bg-white p-2 pb-4 rounded-md">
            <div className="max-md:w-full" data-testid="qr-code">
              <img
                className="max-md:w-full"
                src={`data:image/png;base64,${qrCode}`}
                alt="QR Code"
              />
            </div>
            <div className="flex">
              <p className="text-lg font-semibold">SMART® Health Card</p>
              <img src="/assets/images/SMART.png" className="w-[35px] h-[25px] ml-auto" />
            </div>
          </div>
        ) : (
          <Squirrel />
        )}
        {isIOSMobile && (
          <img
            src="/assets/images/apple_health.png"
            className="w-full"
            onClick={() =>
              window.open(
                `https://redirect.health.apple.com/SMARTHealthCard/${qrCode.slice(
                  5,
                  qrCode.length
                )}`,
                "_blank",
                "noopener,noreferrer"
              )
            }
          />
        )}
        <div className="flex flex-col">
          <p className="text-xs">
            SMART® Health Cards may contain only a limited set of COVID-19 vaccine doses. For more
            information about SMART® Health Cards:
          </p>
          <a
            className="text-xs underline text-blue-400"
            href="https://smarthealth.cards/"
            target="_blank"
          >
            https://smarthealth.cards/
          </a>
        </div>
        <img src="/assets/images/Docket_Logo_Blue.png" className="w-[120px] h-[36px] ml-auto" />
      </div>
    </div>
  );
}
